import {
  resolveAnnotation,
  resolveDimension,
} from '../../three/core/dimension/DimensionEngine';
import {
  estimateLabelSize,
  resolveLabelOverlaps,
  shouldShowLabel,
  tierDimensionLines,
} from '../../three/core/dimension/DimensionLayout';
import {
  projectDimension,
  projectToScreen,
} from '../../three/core/dimension/ProjectionEngine';
import { formatLength } from '../../utils/dimension/dimensionFormat';

const SVG_NS = 'http://www.w3.org/2000/svg';
const ARROW_SIZE = 8;
const EXT_OVERSHOOT_PX = 4;
const MIN_VISIBLE_LENGTH_PX = 8;
const MIN_ARROW_LENGTH_PX = 22;
const HIT_WIDTH_PX = 14;

const el = (name, attrs = {}) => {
  const node = document.createElementNS(SVG_NS, name);
  Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, v));
  return node;
};

const setAttrs = (node, attrs) => {
  Object.entries(attrs).forEach(([k, v]) => node.setAttribute(k, v));
};

/** Hướng ra xa vật (từ điểm neo tới đường DIM) của một DIM đã chiếu, đơn vị. */
const outDirOf = geo => {
  const dx = (geo.t1.x - geo.s1.x + (geo.t2.x - geo.s2.x)) / 2;
  const dy = (geo.t1.y - geo.s1.y + (geo.t2.y - geo.s2.y)) / 2;
  const len = Math.hypot(dx, dy);
  return len > 1e-6 ? { x: dx / len, y: dy / len } : { x: 0, y: 0 };
};

/** Góc của nhãn luôn đọc được (không lộn ngược): đưa về [-90°, 90°]. */
const readableAngle = angle => {
  let a = angle;
  if (a > Math.PI / 2) a -= Math.PI;
  if (a < -Math.PI / 2) a += Math.PI;
  return a;
};

/**
 * Vẽ DIM lên <svg> bằng DOM thuần (cập nhật mỗi frame, không qua React state).
 * Mọi style dùng attribute (không dùng class/CSS) để serialize ra ảnh export
 * vẫn giữ nguyên giao diện.
 */
export class DimensionSvgRenderer {
  constructor(svg) {
    this.svg = svg;
    this.nodes = new Map();
    // Lớp marker (điểm snap, điểm đã chọn): không bắt chuột, bỏ khi export
    this.markerLayer = el('g', {
      'data-export': 'skip',
      'pointer-events': 'none',
    });
    this.markerKey = '';
    svg.appendChild(this.markerLayer);
  }

  createNodes(id) {
    const g = el('g', { 'data-dim-id': id });
    const nodes = {
      g,
      ext1: el('line'),
      ext2: el('line'),
      line: el('line'),
      // Vùng bắt chuột (trong suốt, dày hơn) để dễ kéo đường DIM
      hit: el('line', {
        'data-part': 'line',
        'data-export': 'skip',
        stroke: 'transparent',
        'stroke-width': HIT_WIDTH_PX,
        'pointer-events': 'stroke',
        style: 'cursor: move',
      }),
      arrow1: el('polygon'),
      arrow2: el('polygon'),
      text: el('text', {
        'data-part': 'text',
        'pointer-events': 'all',
        style: 'cursor: grab; user-select: none',
        'text-anchor': 'middle',
        'font-family': 'Arial, Helvetica, sans-serif',
        'font-weight': '600',
        'paint-order': 'stroke',
        stroke: '#ffffff',
        'stroke-width': '3',
        'stroke-linejoin': 'round',
      }),
    };
    [
      nodes.ext1,
      nodes.ext2,
      nodes.line,
      nodes.hit,
      nodes.arrow1,
      nodes.arrow2,
      nodes.text,
    ].forEach(n => g.appendChild(n));
    this.svg.appendChild(g);
    this.nodes.set(id, nodes);
    return nodes;
  }

  removeNodes(id) {
    const nodes = this.nodes.get(id);
    if (!nodes) return;
    nodes.g.remove();
    this.nodes.delete(id);
  }

  /**
   * @param {object[]} items - danh sách DIM
   * @param {{camera, width, height, getGroup, unit, selectedId,
   *          interactive?: boolean, markers?: object[]}} ctx
   *  - interactive=false (đang ở chế độ đo thủ công): đường DIM và chữ không
   *    bắt chuột để click xuyên xuống canvas 3D
   *  - markers: các điểm vẽ thêm [{x, y, shape, color, label}] (toạ độ pixel)
   *  - item có `preview: true` là DIM xem trước, vẽ mờ và không bắt chuột
   * @returns {string[]} id các DIM mất anchor (module đã bị xoá)
   */
  render(items, ctx) {
    const {
      camera,
      width,
      height,
      getGroup,
      unit = 'mm',
      selectedId,
      interactive = true,
      markers = [],
      getPartMeshes,
      layers,
    } = ctx;
    const resolveOpts = { getPartMeshes };
    const orphanIds = [];
    setAttrs(this.svg, {
      width,
      height,
      viewBox: `0 0 ${width} ${height}`,
    });

    // ---- Pass 1: giải từng DIM / ghi chú ra hình học trên màn hình ----
    const alive = new Set();
    const linear = [];
    const notes = [];
    items.forEach(dim => {
      const isNote = dim.type === 'annotation';
      if (dim.type !== 'linear' && !isNote) return;
      alive.add(dim.id);
      const nodes =
        this.nodes.get(dim.id) ??
        (isNote ? this.createNoteNodes(dim.id) : this.createNodes(dim.id));
      const shown = dim.visible && layers?.[dim.group] !== false;

      // Luôn giải (kể cả đang ẩn) để biết DIM nào đã mất module / bộ phận
      if (isNote) {
        const resolved = resolveAnnotation(dim, getGroup, resolveOpts);
        if (!resolved && !dim.preview) orphanIds.push(dim.id);
        const p =
          resolved && shown
            ? projectToScreen(resolved.point, camera, width, height)
            : null;
        if (!p || !p.visible) {
          nodes.g.setAttribute('display', 'none');
          return;
        }
        nodes.g.removeAttribute('display');
        notes.push({ dim, nodes, p });
        return;
      }

      const resolved = resolveDimension(dim, getGroup, resolveOpts);
      if (!resolved && !dim.preview) orphanIds.push(dim.id);
      const geo =
        resolved && shown
          ? projectDimension(resolved, camera, width, height)
          : null;
      if (!geo || !geo.visible || geo.lengthPx < MIN_VISIBLE_LENGTH_PX) {
        nodes.g.setAttribute('display', 'none');
        return;
      }
      nodes.g.removeAttribute('display');
      linear.push({ dim, nodes, resolved, geo });
    });

    // ---- Pass 2: bố cục (xếp tầng đường DIM, chống đè nhãn) ----
    const layoutItems = linear.filter(e => !e.dim.preview);
    const shifts = tierDimensionLines(
      layoutItems.map(({ dim, geo }) => ({
        id: dim.id,
        s1: geo.s1,
        s2: geo.s2,
        t1: geo.t1,
        t2: geo.t2,
      }))
    );
    const labels = layoutItems
      .map(entry => {
        const label = this.labelOf(entry, unit, shifts.get(entry.dim.id) ?? 0);
        return (
          label && {
            id: entry.dim.id,
            ...label.rect,
            // DIM đang chọn giữ nguyên vị trí nhãn
            priority: entry.dim.id === selectedId ? -1 : 0,
          }
        );
      })
      .filter(Boolean);
    const nudges = resolveLabelOverlaps(labels);

    // ---- Pass 3: vẽ ----
    linear.forEach(entry => {
      const { dim } = entry;
      this.draw(
        entry.nodes,
        dim,
        entry.resolved,
        entry.geo,
        unit,
        dim.id === selectedId,
        interactive && !dim.preview,
        { shift: shifts.get(dim.id) ?? 0, nudge: nudges.get(dim.id) }
      );
    });
    notes.forEach(({ dim, nodes, p }) =>
      this.drawNote(nodes, dim, p, dim.id === selectedId, interactive)
    );

    [...this.nodes.keys()].forEach(id => {
      if (!alive.has(id)) this.removeNodes(id);
    });
    this.drawMarkers(markers);
    return orphanIds;
  }

  /** Hộp nhãn (toạ độ màn hình, đã tính phần đẩy tầng) để chống đè nhãn. */
  labelOf({ dim, resolved, geo }, unit, shiftPx) {
    if (!shouldShowLabel(geo.lengthPx)) return null;
    const out = outDirOf(geo);
    const { fontSize, decimals } = dim.style;
    const [sx, sy] = dim.labelShift ?? [0, 0];
    const text = formatLength(resolved.valueM, { unit, decimals });
    const { w, h } = estimateLabelSize(text, fontSize);
    return {
      rect: {
        x: geo.mid.x + out.x * shiftPx + sx,
        // Chữ nằm phía trên đường DIM ~4px: tâm hộp cao hơn đường một chút
        y: geo.mid.y + out.y * shiftPx + sy - 4 - fontSize * 0.35,
        w,
        h,
        angle: readableAngle(geo.angle),
      },
    };
  }

  createNoteNodes(id) {
    const g = el('g', { 'data-dim-id': id, 'data-kind': 'note' });
    const nodes = {
      g,
      leader: el('line'),
      arrow: el('polygon'),
      box: el('rect', {
        'data-part': 'text',
        'pointer-events': 'all',
        style: 'cursor: grab',
        rx: 3,
      }),
      text: el('text', {
        'data-part': 'text',
        'pointer-events': 'all',
        style: 'cursor: grab; user-select: none',
        'font-family': 'Arial, Helvetica, sans-serif',
        'font-weight': '600',
      }),
      textKey: '',
    };
    [nodes.leader, nodes.arrow, nodes.box, nodes.text].forEach(n =>
      g.appendChild(n)
    );
    this.svg.appendChild(g);
    this.nodes.set(id, nodes);
    return nodes;
  }

  /**
   * Ghi chú: khung chữ nhiều dòng + đường dẫn kèm mũi tên chỉ vào điểm neo.
   * `labelShift` = vị trí góc trên-trái của khung so với điểm neo (px).
   */
  drawNote(nodes, dim, p, selected, interactive) {
    const { color, fontSize, lineWidth } = dim.style;
    const lines = String(dim.text ?? '').split('\n');
    const lineHeight = fontSize * 1.25;
    const { w: textW } = estimateLabelSize(
      lines.reduce((a, b) => (b.length > a.length ? b : a), ''),
      fontSize
    );
    const boxW = Math.max(textW + 6, 28);
    const boxH = lines.length * lineHeight + 8;
    const [sx, sy] = dim.labelShift ?? [60, -50];
    const bx = p.x + sx;
    const by = p.y + sy;
    const strokeW = selected ? lineWidth + 1 : lineWidth;
    nodes.g.setAttribute('opacity', dim.preview ? '0.7' : '1');

    setAttrs(nodes.box, {
      x: bx,
      y: by,
      width: boxW,
      height: boxH,
      fill: 'rgba(255,255,255,0.94)',
      stroke: color,
      'stroke-width': strokeW,
      'pointer-events': interactive ? 'all' : 'none',
    });

    // Chữ nhiều dòng: dựng lại tspan chỉ khi nội dung / vị trí đổi
    const key = `${dim.text}|${fontSize}|${Math.round(bx)}|${Math.round(by)}`;
    if (nodes.textKey !== key) {
      nodes.textKey = key;
      nodes.text.replaceChildren();
      lines.forEach((line, i) => {
        const tspan = el('tspan', {
          x: bx + 6,
          y: by + 4 + fontSize + i * lineHeight,
        });
        tspan.textContent = line;
        nodes.text.appendChild(tspan);
      });
    }
    setAttrs(nodes.text, {
      fill: color,
      'font-size': fontSize,
      'pointer-events': interactive ? 'all' : 'none',
    });

    // Đường dẫn: từ điểm gần nhất trên viền khung tới điểm neo
    const cx = Math.max(bx, Math.min(bx + boxW, p.x));
    const cy = Math.max(by, Math.min(by + boxH, p.y));
    const inside = cx === p.x && cy === p.y;
    const from = inside
      ? { x: bx + boxW / 2, y: by + boxH / 2 }
      : { x: cx, y: cy };
    setAttrs(nodes.leader, {
      stroke: color,
      'stroke-width': strokeW,
      x1: from.x,
      y1: from.y,
      x2: p.x,
      y2: p.y,
    });
    // Mũi tên tại điểm neo, hướng từ khung tới điểm
    const dx = p.x - from.x;
    const dy = p.y - from.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const bxA = p.x - ux * ARROW_SIZE;
    const byA = p.y - uy * ARROW_SIZE;
    setAttrs(nodes.arrow, {
      fill: color,
      points: `${p.x},${p.y} ${bxA - uy * (ARROW_SIZE / 3)},${byA + ux * (ARROW_SIZE / 3)} ${bxA + uy * (ARROW_SIZE / 3)},${byA - ux * (ARROW_SIZE / 3)}`,
    });
  }

  /** Vẽ marker trên cùng (chỉ dựng lại DOM khi danh sách marker đổi). */
  drawMarkers(markers) {
    // Giữ lớp marker luôn nằm trên các DIM
    if (this.svg.lastChild !== this.markerLayer) {
      this.svg.appendChild(this.markerLayer);
    }
    const key = JSON.stringify(
      markers.map(m => [
        Math.round(m.x * 2),
        Math.round(m.y * 2),
        m.shape,
        m.color,
        m.label,
      ])
    );
    if (key === this.markerKey) return;
    this.markerKey = key;
    this.markerLayer.replaceChildren();
    markers.forEach(m => {
      const color = m.color ?? '#258FD2';
      const size = 5;
      let shape;
      if (m.shape === 'square') {
        shape = el('rect', {
          x: m.x - size,
          y: m.y - size,
          width: size * 2,
          height: size * 2,
        });
      } else if (m.shape === 'diamond') {
        shape = el('polygon', {
          points: `${m.x},${m.y - size - 1} ${m.x + size + 1},${m.y} ${m.x},${m.y + size + 1} ${m.x - size - 1},${m.y}`,
        });
      } else {
        shape = el('circle', { cx: m.x, cy: m.y, r: m.filled ? 4 : size });
      }
      setAttrs(shape, {
        fill: m.filled ? color : 'rgba(255,255,255,0.85)',
        stroke: color,
        'stroke-width': 2,
      });
      this.markerLayer.appendChild(shape);
      if (m.label) {
        const text = el('text', {
          x: m.x + size + 6,
          y: m.y - size - 4,
          fill: color,
          'font-family': 'Arial, Helvetica, sans-serif',
          'font-size': 12,
          'font-weight': 600,
          'paint-order': 'stroke',
          stroke: '#ffffff',
          'stroke-width': 3,
          'stroke-linejoin': 'round',
        });
        text.textContent = m.label;
        this.markerLayer.appendChild(text);
      }
    });
  }

  draw(
    nodes,
    dim,
    resolved,
    geo,
    unit,
    selected,
    interactive = true,
    layout = {}
  ) {
    const { color, fontSize, lineWidth, decimals } = dim.style;
    nodes.g.setAttribute('opacity', dim.preview ? '0.7' : '1');
    const hitMode = interactive ? 'stroke' : 'none';
    nodes.hit.setAttribute('pointer-events', hitMode);
    nodes.text.setAttribute('pointer-events', interactive ? 'all' : 'none');
    const { s1, s2, angle, lengthPx } = geo;
    // Xếp tầng: đẩy đường DIM ra xa vật (chỉ hiển thị, không đổi dữ liệu)
    const out = outDirOf(geo);
    const shiftPx = layout.shift ?? 0;
    const moved = pt => ({
      x: pt.x + out.x * shiftPx,
      y: pt.y + out.y * shiftPx,
    });
    const t1 = moved(geo.t1);
    const t2 = moved(geo.t2);
    const mid = moved(geo.mid);
    const ux = Math.cos(angle);
    const uy = Math.sin(angle);

    const stroke = {
      stroke: color,
      'stroke-width': selected ? lineWidth + 1 : lineWidth,
    };
    // Đường dóng: từ điểm neo tới đường DIM, nhô thêm một đoạn ngắn
    const ext = (node, from, to) => {
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const len = Math.hypot(dx, dy) || 1;
      setAttrs(node, {
        ...stroke,
        x1: from.x,
        y1: from.y,
        x2: to.x + (dx / len) * EXT_OVERSHOOT_PX,
        y2: to.y + (dy / len) * EXT_OVERSHOOT_PX,
      });
    };
    ext(nodes.ext1, s1, t1);
    ext(nodes.ext2, s2, t2);

    setAttrs(nodes.line, { ...stroke, x1: t1.x, y1: t1.y, x2: t2.x, y2: t2.y });
    setAttrs(nodes.hit, { x1: t1.x, y1: t1.y, x2: t2.x, y2: t2.y });

    // Mũi tên: hướng ra ngoài đoạn DIM, thu nhỏ/ẩn khi đoạn quá ngắn
    const showArrows = lengthPx >= MIN_ARROW_LENGTH_PX;
    const arrow = (node, tip, dir) => {
      if (!showArrows) {
        node.setAttribute('display', 'none');
        return;
      }
      node.removeAttribute('display');
      if (dim.style.arrowType === 'tick') {
        // Gạch chéo kiểu bản vẽ kiến trúc: xoay 45° so với đường DIM
        const dx = (ux - uy) / Math.SQRT2;
        const dy = (ux + uy) / Math.SQRT2;
        setAttrs(node, {
          fill: 'none',
          stroke: color,
          'stroke-width': lineWidth * 1.5,
          points: `${tip.x - dx * 5},${tip.y - dy * 5} ${tip.x + dx * 5},${tip.y + dy * 5}`,
        });
        return;
      }
      const bx = tip.x - dir * ux * ARROW_SIZE;
      const by = tip.y - dir * uy * ARROW_SIZE;
      const nx = -uy * (ARROW_SIZE / 3);
      const ny = ux * (ARROW_SIZE / 3);
      setAttrs(node, {
        fill: color,
        stroke: 'none',
        points: `${tip.x},${tip.y} ${bx + nx},${by + ny} ${bx - nx},${by - ny}`,
      });
    };
    arrow(nodes.arrow1, t1, -1);
    arrow(nodes.arrow2, t2, 1);

    // Nhãn: luôn đọc được (không bị lộn ngược), đặt phía trên đường DIM
    let deg = (angle * 180) / Math.PI;
    if (deg > 90) deg -= 180;
    if (deg < -90) deg += 180;
    const [shiftX, shiftY] = dim.labelShift ?? [0, 0];
    const nudge = layout.nudge ?? { dx: 0, dy: 0 };
    const tx = mid.x + shiftX + nudge.dx;
    const ty = mid.y + shiftY + nudge.dy;
    // Đoạn DIM quá ngắn: ẩn chữ (trừ khi đang chọn để vẫn đọc được số)
    nodes.text.textContent =
      selected || shouldShowLabel(lengthPx)
        ? formatLength(resolved.valueM, { unit, decimals })
        : '';
    setAttrs(nodes.text, {
      x: tx,
      y: ty - 4,
      fill: color,
      'font-size': fontSize,
      transform: `rotate(${deg} ${tx} ${ty})`,
    });
  }

  dispose() {
    this.nodes.forEach(n => n.g.remove());
    this.nodes.clear();
    this.markerLayer.remove();
  }
}
